import { MediaGuideMetadata } from '@/utils/media/types';
import { EventProperties } from '@calm/evexia-analytics';
import { MediaTool, WorkflowWorkflowInfo } from '@calm/evexia-web-sdk';
import { AudioPlayerOpenState } from '@rhg/components/media/audio-player-combined/types';
import { ControlButtonType } from '@rhg/components/media/video-player/types';
import { MediaTrackerFn } from '@rhg/hooks/use-media-tracker/types';

export enum PlayerMode {
    Dialog = 'dialog',
    Drawer = 'drawer',
}

/** Attribution sent with every progress PATCH for the session (e.g. an emotions check-in). */
export interface GuideProgressSource {
    source: string;
    sourceAttributes?: Record<string, string>;
}

/** Player lifecycle moments surfaces can react to (e.g. to ask for feedback). */
export interface GuidePlayerEvent {
    type: 'ended' | 'closed';
    guideId: string;
}

export enum QueueSourceKind {
    Program = 'program',
    Playlist = 'playlist',
}

export interface ProgramQueueSource {
    kind: QueueSourceKind.Program;
}

export interface PlaylistQueueSource {
    kind: QueueSourceKind.Playlist;
    playlistId: number;
    playlistName: string;
    /** Playable tracks in playlist order. */
    guides: MediaGuideMetadata[];
    /** Pack class of each track's program, keyed by programId. */
    packClassByProgramId: Record<string, string | undefined>;
}

export type QueueSource = ProgramQueueSource | PlaylistQueueSource;

export type OpenGuidePlayerProps = {
    guide: MediaGuideMetadata;
    /** Overrides the screen-name `source` on progress updates for this session */
    progressSource?: GuideProgressSource;
    packClass: string | undefined;
    openState?: AudioPlayerOpenState;
    tools?: MediaTool[];
    workflow?: WorkflowWorkflowInfo;
    trackFn?: MediaTrackerFn;
    willSkipToPreviousProgress?: boolean;
    sessionTrackProps?: EventProperties;
    /** Open the queue already shuffled (header Shuffle CTA starts shuffled playback). */
    willShuffle?: boolean;
    /** Where the queue comes from. Omitted means the playing guide's program. */
    queueSource?: QueueSource;
};

export interface GuidePlayerState {
    openState: AudioPlayerOpenState;
    playingGuide: MediaGuideMetadata | null;
    openGuidePlayer: (props: OpenGuidePlayerProps) => Promise<void>;
    closeGuidePlayer: () => void;
    onMinimize: () => void;
    /**
     * Schedule a deferred minimize-for-navigation: the player switches to
     * `PlayerMode.Drawer` after a short delay so it doesn't flash to drawer
     * before the link click navigates away. Owned here (not in the dialog)
     * so the dialog doesn't need to reach into this provider.
     */
    minimizeForNavigation: () => void;
    playerHeight: number;
    /** Attribution for the current session's progress updates, if any */
    progressSource?: GuideProgressSource;
    /** Listen for the current session ending or the player being closed */
    subscribe: (listener: (event: GuidePlayerEvent) => void) => () => void;
    /** Source of the loaded queue, or undefined when nothing is playing. */
    queueSource: QueueSource | undefined;
    /**
     * Reports a pause or resume made outside the player's own controls to the
     * open player's session analytics. Does nothing when no player is open.
     */
    trackPlaybackToggle: (buttonType: ControlButtonType) => void;
    /** Lets the open player receive `trackPlaybackToggle` calls; pass undefined to stop. */
    registerPlaybackToggleTracker: (
        tracker: ((buttonType: ControlButtonType) => void) | undefined
    ) => void;
}
